import { useState } from 'react'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAlertHistory } from '@/hooks/use-alerts'

const PER_PAGE = 25

const SEVERITY_OPTIONS = ['all', 'warning', 'critical'] as const

export function AlertHistoryTable(): React.JSX.Element {
  const [page, setPage] = useState(1)
  const [severityFilter, setSeverityFilter] = useState<string>('all')

  const historyQuery = useAlertHistory({
    page,
    per_page: PER_PAGE,
    severity: severityFilter === 'all' ? undefined : severityFilter,
  })

  const data = historyQuery.data?.data ?? []
  const total = historyQuery.data?.total ?? 0

  const columns = [
    {
      key: 'created_at',
      label: 'Timestamp',
      render: (row: (typeof data)[0]) => new Date(row.created_at).toLocaleString(),
    },
    {
      key: 'alert_type',
      label: 'Type',
      render: (row: (typeof data)[0]) => (
        <span className="font-medium">{row.alert_type.replace(/_/g, ' ')}</span>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (row: (typeof data)[0]) => <StatusBadge status={row.severity} />,
    },
    {
      key: 'message',
      label: 'Message',
      render: (row: (typeof data)[0]) => <span className="max-w-md truncate">{row.message}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: (typeof data)[0]) => (
        <StatusBadge status={row.resolved_at ? 'resolved' : 'active'}>
          {row.resolved_at ? 'Resolved' : 'Active'}
        </StatusBadge>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-primary">Alert History</h2>
        <select
          value={severityFilter}
          onChange={(e) => {
            setSeverityFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm text-text-primary"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt === 'all' ? 'All severities' : opt.charAt(0).toUpperCase() + opt.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <DataTable
        columns={columns}
        data={data}
        isLoading={historyQuery.isLoading}
        emptyMessage="No alerts fired yet"
        pagination={{ page, perPage: PER_PAGE, total }}
        onPageChange={setPage}
      />
    </div>
  )
}
